import {
  ATTENDANCE_PUNCHES,
  DRIVER_PROFILES,
  deriveTimesheetsFromPunches,
  TIMESHEET_ALLOWANCES,
} from './mockDriverErpData';
import { computeAutomatedPayroll, formatPayrollMonth } from './driverPayrollUtils';

/** Simulated network latency (ms) — remove when wiring real API */
const MOCK_LATENCY_MS = 850;

/**
 * @typedef {import('./payrollCalculations').PayrollDriverRecord} PayrollDriverRecord
 */

/**
 * @typedef {Object} PayrollApiResponse
 * @property {number} year
 * @property {number} month
 * @property {string} monthLabel
 * @property {PayrollDriverRecord[]} drivers
 * @property {string} fetchedAt
 */

function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function mapProfileToPayrollRecord(driver, punches, timesheets, year, month) {
  const engine = computeAutomatedPayroll(driver, punches, timesheets, year, month);
  const { computed } = engine;

  return {
    id: driver.id,
    name: driver.name,
    baseSalary: computed.baseSalary,
    tripsCompleted: computed.tripCount,
    otHours: computed.totalOvertimeHours,
    penalties: computed.penaltyDeductions,
  };
}

/**
 * Mock async payroll fetch — simulates GET /api/payroll/drivers?year=&month=
 *
 * Swap implementation for production:
 * ```js
 * import axios from 'axios';
 * export async function fetchPayrollData({ year, month }) {
 *   const { data } = await axios.get('/api/payroll/drivers', { params: { year, month } });
 *   return data;
 * }
 * ```
 *
 * @param {{ year?: number, month?: number, signal?: AbortSignal }} [options]
 * @returns {Promise<PayrollApiResponse>}
 */
export async function fetchPayrollData({ year = 2026, month = 5, signal } = {}) {
  await delay(MOCK_LATENCY_MS);

  if (signal?.aborted) {
    throw new DOMException('Payroll fetch aborted', 'AbortError');
  }

  const punches = ATTENDANCE_PUNCHES;
  const timesheets = deriveTimesheetsFromPunches(punches, TIMESHEET_ALLOWANCES);

  const drivers = DRIVER_PROFILES.map((driver) =>
    mapProfileToPayrollRecord(driver, punches, timesheets, year, month),
  );

  return {
    year,
    month,
    monthLabel: formatPayrollMonth(year, month),
    drivers,
    fetchedAt: new Date().toISOString(),
  };
}
